'use client'

import { useState, useRef, useEffect } from 'react'
import { Bot, X, ArrowUp } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Text } from '@/components/ui/text'
import { useModal } from './ModalContext'

interface Message {
  text: string
  isUser: boolean
}

export default function ModalChat() {
  const { activeModal, closeModal } = useModal()
  const isOpen = activeModal === 'chat'
  const t = useTranslations('modals.chat')

  const initialMessages: Message[] = [
    { text: (t.raw('initialMessages') as string[])[0], isUser: false },
    { text: (t.raw('initialMessages') as string[])[1], isUser: false },
  ]

  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [input, setInput] = useState('')
  const [typing, setTyping] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, typing])

  const send = () => {
    const text = input.trim()
    if (!text) return
    setMessages((m) => [...m, { text, isUser: true }])
    setInput('')
    setTyping(true)
    setTimeout(() => {
      setTyping(false)
      setMessages((m) => [
        ...m,
        { text: t('typingReply'), isUser: false },
      ])
    }, 1200)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-text-primary/40 backdrop-blur-sm"
        onClick={closeModal}
      />
      <div className="relative z-10 flex h-[min(620px,90vh)] w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-border bg-white shadow-[0_40px_80px_-20px_rgba(43,90,255,0.25)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-grad-cta text-white">
              <Bot size={16} strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-display text-base font-semibold text-text-primary">Infiteq AI</div>
              <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" /> {t('online')}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={closeModal}
            aria-label={t('closeLabel')}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-text-muted transition-colors hover:border-blue hover:text-blue"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 space-y-3 overflow-y-auto bg-bg-off px-5 py-5">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex items-end gap-2 ${msg.isUser ? 'flex-row-reverse' : 'flex-row'}`}
            >
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  msg.isUser
                    ? 'bg-white border border-border text-text-muted text-xs font-medium'
                    : 'bg-grad-cta text-white'
                }`}
              >
                {msg.isUser ? t('userLabel') : <Bot size={14} strokeWidth={1.75} />}
              </div>
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-[14px] leading-[1.55] ${
                  msg.isUser
                    ? 'bg-grad-cta text-white rounded-br-sm'
                    : 'bg-white text-text-primary border border-border rounded-bl-sm'
                }`}
              >
                {msg.text}
              </div>
            </div>
          ))}
          {typing && (
            <div className="flex items-end gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-grad-cta text-white">
                <Bot size={14} strokeWidth={1.75} />
              </div>
              <div className="rounded-2xl rounded-bl-sm border border-border bg-white px-4 py-3">
                <div className="flex items-center gap-1">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-subtle [animation-delay:-0.3s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-subtle [animation-delay:-0.15s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-subtle" />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-border bg-white px-5 py-4">
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="w-full rounded-full border border-border bg-bg-off px-4 py-2.5 text-[14px] text-text-primary placeholder:text-text-subtle focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/20"
              placeholder={t('placeholder')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
            />
            <button
              type="button"
              onClick={send}
              aria-label={t('sendLabel')}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-grad-cta text-white shadow-[0_10px_30px_-10px_rgba(43,90,255,0.5)] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-10px_rgba(124,47,232,0.6)] disabled:opacity-50"
            >
              <ArrowUp size={16} strokeWidth={2.5} />
            </button>
          </div>
          <Text tone="subtle" size="xs" className="mt-2 text-center">
            {t('footer')}
          </Text>
        </div>
      </div>
    </div>
  )
}
